import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {Field} from '../../interfaces/field';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {FieldErrorService} from '../../services/field-error.service';

declare const intlTelInput: any;
declare const intlTelInputUtils: any;

@Component({
    selector: 'app-phone-field',
    templateUrl: './phone-field.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./phone-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PhoneFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: Field;
  @Input() formControlName: UntypedFormControl;
  @Input() control: UntypedFormGroup;

  input: HTMLInputElement;
  iti: any;
  isBlurred: boolean;
  phoneNumber: string;
  valid: boolean;
  hasErrors = false;
  private destroyed = false;

  constructor(private errorService: FieldErrorService, private host: ElementRef) {
    this.phoneNumber = '';
  }

  ngOnInit() {
    this.valid = true;
  }

  validateAndPatchValue(event: any): void {
    // console.groupCollapsed('PhoneNumber validateAndPatchValue', this.config.property)

    if (this.iti.getNumber(intlTelInputUtils.numberFormat.E164)) {
      this.phoneNumber = this.iti.getNumber(intlTelInputUtils.numberFormat.E164).replace(/ /g, '');
      this.valid = this.iti.isValidNumber();
      if (typeof (this.phoneNumber['phoneNumber']) !== 'undefined') {
        this.valid = false;
      }

      const errorId = `${this.config.property}Invalid`;
      if (this.phoneNumber !== '') {
        this.errorService.removeError(`${this.config.property}Required`);
      }

      if (this.isBlurred) {
        if (!this.valid) {
          this.errorService.addError({
            id: errorId,
            property: this.config.property,
            text: 'phone_number_field_error_invalid',
            label: this.config.label,
          });
        } else {
          this.errorService.removeError(errorId);
        }
      }

      const patch = {};
      if (this.valid) {
        patch[this.config.property] = this.valid ? this.phoneNumber : '';
        patch[this.config.property + 'Country'] = this.iti.getSelectedCountryData().iso2;
        this.control.patchValue(patch);
      }
    }
    // console.groupEnd();
  }

  ngAfterViewInit(): void {
    const self = this;
    // Scope the lookup to THIS component's host, not the whole document. A
    // document-wide query let a stale retry from a torn-down instance grab a
    // live sibling's input and initialise it a second time.
    this.input = this.host.nativeElement.querySelector(`input[type=tel]`);
    if (this.input) {
      // Bail out if intl-tel-input has already wrapped this input. Without this
      // guard a second ngAfterViewInit pass (or a stale retry) calls
      // intlTelInput() again on the same element, stacking two flag pickers so
      // the flag, dial code and number desync when you pick a country.
      if (this.iti || this.input.closest('.iti')) {
        return;
      }

      this.input.addEventListener('countrychange', this.validateAndPatchValue.bind(this));

      // intl-tel-input v18 matches `initialCountry` against its ISO2 list case-SENSITIVELY
      // and silently falls back to the United States (+1) on a miss — it does not warn.
      // Portal configs store the country uppercase ("NL"), so it must be folded to lower
      // case here or every such form opens on +1 instead of its own country.
      const configuredCountry = (this.config.defaultCountry || '').toLowerCase();
      if (!configuredCountry) {
        this.config.defaultCountry = 'nl';
      } else if (configuredCountry === 'en') {
        this.config.defaultCountry = 'gb';
      } else {
        this.config.defaultCountry = configuredCountry;
      }
      setTimeout(() => {
        if (this.iti || this.input.closest('.iti')) {
          return;
        }
        if (typeof intlTelInput !== 'undefined') {
          this.iti = intlTelInput(this.input, {
            utilsScript: 'assets/js/intlTelInputUtils.js',
            // The form's configured country is the DEFAULT selection (e.g. a Malta form
            // opens on Malta), so most users never open the list. `preferredCountries` is
            // intentionally omitted: in intl-tel-input v18 it is ignored once countrySearch
            // is enabled, so pinning a country to the top of the list is not possible
            // alongside the search box — and the search box is the higher-priority fix.
            initialCountry: this.config.defaultCountry,
            separateDialCode: true,
            formatOnDisplay: false,
            // v18: type-to-filter search box in the dropdown so users don't have to
            // scroll an alphabetical list (was unusable on mobile).
            countrySearch: true,
            // v18: on real mobile devices render the country list as a fullscreen
            // overlay instead of an anchored dropdown, so it can't overlap adjacent
            // fields (the textarea / nav buttons beneath the phone field).
            useFullscreenPopup: true,
          });

          // Take the injected country selector out of the tab order so Tab moves
          // straight from the previous field (e.g. email) to the phone number
          // input, skipping the flag/dial-code picker. It stays clickable for
          // users who need to change the country.
          const flag = document.querySelector(`#phone-field-${this.config.property} .iti__selected-flag`);
          if (flag) {
            flag.setAttribute('tabindex', '-1');
          }
        } else {
          console.error('intlTelInput is not loaded.');
        }
      }, 0);
    } else if (!this.destroyed) {
      // Retry until the input renders, but stop once the component is torn down
      // so a field destroyed before its input appears doesn't leave a timer
      // re-invoking ngAfterViewInit forever.
      setTimeout(function () {
        self.ngAfterViewInit();
      }, 100)
    }
  }

  validityChanged(valid: boolean) {
    this.hasErrors = valid;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // Destroy the intl-tel-input instance so its injected DOM/listeners are
    // removed when the field is recreated (e.g. stepping through the form).
    // Otherwise the orphaned instance lingers and stacks on re-init.
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
  }

}
